import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import type { RoleCheck } from '../../Types/Auth/Auth.types';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: RoleCheck;
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * Componente para proteger rutas o componentes basado en roles
 * 
 * @example
 * ```tsx
 * <RoleGuard allowedRoles="admin">
 *   <AdminPanel />
 * </RoleGuard>
 * 
 * <RoleGuard allowedRoles={['admin', 'lawyer']} redirectTo="/unauthorized">
 *   <ManagementPanel />
 * </RoleGuard>
 * ```
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallback,
  redirectTo,
}) => {
  const { isAuthenticated, hasRole, isLoading } = useAuth();

  // Mostrar loading mientras se verifica autenticación
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir al login
  if (!isAuthenticated) {
    return <Navigate to={`${process.env.PUBLIC_URL}/login`} replace />;
  }

  // Verificar si tiene el rol requerido
  if (!hasRole(allowedRoles)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <h3>Acceso Denegado</h3>
          <p>No tienes permisos para acceder a esta sección.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

